import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type TooltipSide = "top" | "bottom" | "left" | "right";
export type TooltipAlign = "start" | "center" | "end";

export interface TooltipProps {
  /** The trigger element */
  children: React.ReactNode;
  /** Tooltip text */
  content: React.ReactNode;
  /** Preferred placement (auto-flips when viewport space is insufficient) */
  side?: TooltipSide;
  /** Alignment along the cross axis */
  align?: TooltipAlign;
  /** Delay before tooltip appears (ms) */
  delayMs?: number;
  /** Controlled — if provided, tooltip visibility is externally managed */
  open?: boolean;
  /** className applied to tooltip bubble */
  className?: string;
  /** Disables the tooltip without removing the trigger wrapper */
  disabled?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const GAP = 8; // px between trigger and bubble
const VIEWPORT_PADDING = 8; // min px from viewport edge

// ── Position calculation ──────────────────────────────────────────────────────

interface Coords {
  top: number;
  left: number;
  actualSide: TooltipSide;
}

function calcCoords(
  trigger: DOMRect,
  bubble: DOMRect,
  side: TooltipSide,
  align: TooltipAlign,
): Coords {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Try preferred side, then flip if not enough room
  let actualSide = side;
  if (side === "top" && trigger.top - bubble.height - GAP < VIEWPORT_PADDING) {
    actualSide = "bottom";
  } else if (side === "bottom" && trigger.bottom + bubble.height + GAP > vh - VIEWPORT_PADDING) {
    actualSide = "top";
  } else if (side === "left" && trigger.left - bubble.width - GAP < VIEWPORT_PADDING) {
    actualSide = "right";
  } else if (side === "right" && trigger.right + bubble.width + GAP > vw - VIEWPORT_PADDING) {
    actualSide = "left";
  }

  let top = 0;
  let left = 0;

  if (actualSide === "top" || actualSide === "bottom") {
    top =
      actualSide === "top"
        ? trigger.top - bubble.height - GAP
        : trigger.bottom + GAP;

    if (align === "start") {
      left = trigger.left;
    } else if (align === "end") {
      left = trigger.right - bubble.width;
    } else {
      left = trigger.left + trigger.width / 2 - bubble.width / 2;
    }
  } else {
    left =
      actualSide === "left"
        ? trigger.left - bubble.width - GAP
        : trigger.right + GAP;

    if (align === "start") {
      top = trigger.top;
    } else if (align === "end") {
      top = trigger.bottom - bubble.height;
    } else {
      top = trigger.top + trigger.height / 2 - bubble.height / 2;
    }
  }

  // Clamp to viewport
  left = Math.max(VIEWPORT_PADDING, Math.min(left, vw - bubble.width - VIEWPORT_PADDING));
  top  = Math.max(VIEWPORT_PADDING, Math.min(top,  vh - bubble.height - VIEWPORT_PADDING));

  return { top, left, actualSide };
}

// ── Arrow offset helper ───────────────────────────────────────────────────────

function arrowStyle(side: TooltipSide): React.CSSProperties {
  const base: React.CSSProperties = {
    position: "absolute",
    width: 0,
    height: 0,
  };

  switch (side) {
    case "top":
      return { ...base, bottom: -4, left: "50%", transform: "translateX(-50%)",
        borderLeft: "5px solid transparent", borderRight: "5px solid transparent",
        borderTop: "5px solid var(--color-surface-tooltip, #1e1b4b)" };
    case "bottom":
      return { ...base, top: -4, left: "50%", transform: "translateX(-50%)",
        borderLeft: "5px solid transparent", borderRight: "5px solid transparent",
        borderBottom: "5px solid var(--color-surface-tooltip, #1e1b4b)" };
    case "left":
      return { ...base, right: -4, top: "50%", transform: "translateY(-50%)",
        borderTop: "5px solid transparent", borderBottom: "5px solid transparent",
        borderLeft: "5px solid var(--color-surface-tooltip, #1e1b4b)" };
    case "right":
      return { ...base, left: -4, top: "50%", transform: "translateY(-50%)",
        borderTop: "5px solid transparent", borderBottom: "5px solid transparent",
        borderRight: "5px solid var(--color-surface-tooltip, #1e1b4b)" };
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Tooltip({
  children,
  content,
  side = "top",
  align = "center",
  delayMs = 500,
  open: controlledOpen,
  className,
  disabled = false,
}: TooltipProps) {
  const triggerRef  = useRef<HTMLSpanElement>(null);
  const bubbleRef   = useRef<HTMLDivElement>(null);
  const timerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [internalOpen, setInternalOpen] = useState(false);
  const [coords, setCoords]             = useState<Coords | null>(null);
  const [visible, setVisible]           = useState(false);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const updateCoords = useCallback(() => {
    if (!triggerRef.current || !bubbleRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const bubbleRect  = bubbleRef.current.getBoundingClientRect();
    setCoords(calcCoords(triggerRect, bubbleRect, side, align));
  }, [side, align]);

  // When tooltip becomes open, mount → measure → show
  useEffect(() => {
    if (isOpen) {
      // Measure on next frame so bubble has rendered dimensions
      const frame = requestAnimationFrame(() => {
        updateCoords();
        setVisible(true);
      });
      return () => cancelAnimationFrame(frame);
    } else {
      setVisible(false);
      // Leave animation
      const t = setTimeout(() => setCoords(null), 150);
      return () => clearTimeout(t);
    }
  }, [isOpen, updateCoords]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [isOpen, updateCoords]);

  const handleMouseEnter = () => {
    if (disabled || controlledOpen !== undefined) return;
    timerRef.current = setTimeout(() => setInternalOpen(true), delayMs);
  };

  const handleMouseLeave = () => {
    if (controlledOpen !== undefined) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setInternalOpen(false);
  };

  const handleFocus = () => {
    if (disabled || controlledOpen !== undefined) return;
    setInternalOpen(true);
  };

  const handleBlur = () => {
    if (controlledOpen !== undefined) return;
    setInternalOpen(false);
  };

  const actualSide = coords?.actualSide ?? side;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="inline-flex"
        aria-describedby={isOpen ? "tooltip-bubble" : undefined}
      >
        {children}
      </span>

      {(isOpen || coords !== null) &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={bubbleRef}
            id="tooltip-bubble"
            role="tooltip"
            className={cn(
              "fixed z-[1070] max-w-[240px] px-2.5 py-1.5",
              "rounded-md text-xs leading-snug font-medium",
              "bg-zinc-900 text-white dark:bg-zinc-700 dark:text-zinc-50",
              "pointer-events-none select-none",
              "transition-opacity duration-150",
              visible ? "opacity-100" : "opacity-0",
              className,
            )}
            style={
              coords
                ? { top: coords.top, left: coords.left }
                : { top: -9999, left: -9999 }
            }
          >
            {content}
            <span aria-hidden="true" style={arrowStyle(actualSide)} />
          </div>,
          document.body,
        )}
    </>
  );
}

Tooltip.displayName = "Tooltip";
